/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { types, Instance, getEnv, SnapshotIn } from 'mobx-state-tree';
import sanityClient, { SanityClient } from '@sanity/client';
import urlBuilder from '@sanity/image-url';
import { SanityImageSource } from '@sanity/image-url/lib/types/types';
import { Dataset, datasets, Feature, FeatureFaq } from './types';
import { makeAsyncActions, makeAsyncViews } from '../asyncUtils';
import { ImageUrlBuilder } from '@sanity/image-url/lib/types/builder';
import { LazyObservablePromise } from '../LazyObservablePromise';
import { isProduction } from '../../config';

import * as Sentry from '@sentry/react';

const useDevDataset = () =>
    !isProduction() && process.env.REACT_APP_SANITY_ENV !== 'prod';

const isOnStaticBuild = () => typeof window === 'undefined';

const makeClient = (dataset: Dataset) =>
    sanityClient({
        projectId: '68gmuo7j',
        dataset,
        apiVersion: 'v1',
        // No CDN in SSR to avoid caching outdated results
        useCdn: !isOnStaticBuild(),
    });

const makeClientV2 = (dataset: Dataset) =>
    sanityClient({
        projectId: '68gmuo7j',
        dataset,
        apiVersion: 'v2021-10-21',
        useCdn: !isOnStaticBuild(),
    });

const client = datasets.reduce(
    (obj, dataset) => ({
        ...obj,
        [dataset]: makeClient(dataset),
    }),
    {}
) as Record<Dataset, SanityClient>;

const clientV2 = datasets.reduce(
    (obj, dataset) => ({
        ...obj,
        [dataset]: makeClientV2(dataset),
    }),
    {}
) as Record<Dataset, SanityClient>;

const imageUrl = (
    asset: SanityImageSource,
    dataset: Dataset = 'landing_page'
): ImageUrlBuilder => {
    const imageBuilder = urlBuilder(client[useDevDataset() ? 'dev' : dataset]);
    return imageBuilder.image(asset);
};

function safeImageUrl(
    image: SanityImageSource,
    dataset: Dataset = 'landing_page'
): ImageUrlBuilder | null {
    if (!image) return null;
    try {
        return imageUrl(image, dataset) ?? null;
    } catch (error) {
        Sentry.captureException(error);
        return null;
    }
}

export const getImageDimensions = (url: string) => {
    // Url has a format https://<location>-<width>x<height>.<ext>
    const [width, height] = url
        .split('.')
        .slice(-2)[0]
        .split('-')
        .slice(-1)[0]
        .split('x');
    return { width, height };
};

const SanityFeatureFaqModel = types.model('SanityFeatureFaq', {
    feature: types.identifier,
    faq: types.string,
});

const SanityBlogModel = types.model('SanityBlog', {
    blog: types.identifier,
    content: types.string,
});

const SanityPageModel = types.model('SanityPage', {
    page: types.identifier,
    content: types.string,
});

const SanityFeatureDemoModel = types.model('SanityFeatureDemo', {
    featureDemo: types.identifier,
    content: types.string,
});

export const BLOG_POSTS_PAGE_SIZE = 12;

const SanityControllerModel = types
    .model('SanityController', {
        featureDemo: types.map(SanityFeatureDemoModel),
        app: types.maybe(types.string),
        pages: types.map(SanityPageModel),
        rentalTraining: types.maybe(types.string),
        rentalTrainingContent: types.maybe(types.string),
        header: types.maybe(types.string),
        footer: types.maybe(types.string),
        poll: types.maybe(types.string),
        faqs: types.map(SanityFeatureFaqModel),
        blogs: types.map(SanityBlogModel),
        loadedAllPages: types.optional(types.boolean, () => false),
    })
    // eslint-disable-next-line max-lines-per-function
    .actions(self => ({
        setFeatureDemo(slug: string, featureDemo: any) {
            self.featureDemo.put(
                SanityFeatureDemoModel.create({
                    featureDemo: slug,
                    content: JSON.stringify(featureDemo),
                })
            );
            return featureDemo;
        },
        setPage(slug: string, page: any) {
            self.pages.put(
                SanityPageModel.create({
                    page: slug,
                    content: JSON.stringify(page),
                })
            );
            return page;
        },
        setApp(app: any) {
            self.app = JSON.stringify(app);
            return app;
        },
        setRentalTraining(rentalTraining: any) {
            self.rentalTraining = JSON.stringify(rentalTraining);
            return rentalTraining;
        },
        setRentalTrainingContent(rentalTrainingContent: any) {
            self.rentalTrainingContent = JSON.stringify(rentalTrainingContent);
            return rentalTrainingContent;
        },
        setHeader(header: any) {
            self.header = JSON.stringify(header);
            return header;
        },
        setFooter(footer: any) {
            self.footer = JSON.stringify(footer);
            return footer;
        },
        setPoll(poll: any) {
            self.poll = JSON.stringify(poll);
            return poll;
        },
        setFaq(feature: string, faq: any) {
            self.faqs.put(
                SanityFeatureFaqModel.create({
                    feature: feature,
                    faq: JSON.stringify(faq),
                })
            );
            return faq;
        },
        setBlog(blog: string, content: any): any {
            self.blogs.put(
                SanityBlogModel.create({
                    blog: blog,
                    content: JSON.stringify(content),
                })
            );
            return content;
        },
    }))
    .actions(self => ({
        setPages(pages: any) {
            self.loadedAllPages = true;
            pages.forEach((p: any) => self.setPage(p.slug.current, p));
        },
    }))
    .actions(self =>
        makeAsyncActions({
            asyncQuery(query: string, dataset: Dataset) {
                return getEnv(self).client[
                    useDevDataset() ? 'dev' : dataset
                ].fetch(query);
            },
            asyncQueryV2(query: string, dataset: Dataset) {
                return getEnv(self).clientV2[
                    useDevDataset() ? 'dev' : dataset
                ].fetch(query);
            },
        })
    )
    .actions(self =>
        makeAsyncActions({
            asyncPageQuery(slug: string) {
                return self
                    .asyncQuery(
                        '*[_type == "query" && slug.current == "landing_page"][0]{groq}',
                        'landing_page'
                    )
                    .later(({ groq }) => {
                        const newGroq = groq.replace(
                            '*[_type == "page"]{',
                            `*[_type == "page" && slug.current == "${slug}"]{`
                        );
                        return self.asyncQuery(newGroq, 'landing_page');
                    });
            },
        })
    )
    .actions(self =>
        makeAsyncActions({
            asyncSearchTraining(query: string) {
                return self.asyncQueryV2(
                    `
                    *[
                        _type == "trainingContent"
                    ] {
                        ...,
                        'plaintext': pt::text(content),
                        'content': content[] {
                            _type == 'video' => @->{
                                ...,
                                poster->,
                                sources[] {
                                    device,
                                    format,
                                    "url": coalesce(source.asset->fileURL, url)
                                }
                            },
                        _type == 'imageRef' => @->{..., image{..., asset->}},
                        _type == 'block' => @{...},
                        }
                    }
                    |score(
                        boost(title match "*${query}*", 15),
                        plaintext match "*${query}*"
                    )
                    |order(_score desc)
                    [ _score > 0 ]
                `,
                    'landing_page'
                );
            },
        })
    )
    // eslint-disable-next-line max-lines-per-function
    .views(self =>
        makeAsyncViews({
            asyncFeatureDemo(feature: Feature) {
                const existingFeatureDemo = self.featureDemo.get(feature);

                if (existingFeatureDemo) {
                    return JSON.parse(existingFeatureDemo.content);
                }

                return self
                    .asyncQuery(
                        `*[_type == "query" && slug.current == "sections"][0]{groq}`,
                        'landing_page'
                    )
                    .later(({ groq }) =>
                        self
                            .asyncQuery(
                                `*[_type == "appFeatureDemo" && slug.current == "${feature}"][0]{
                                "sections": sections[]->{${groq}}
                             }`,
                                'landing_page'
                            )
                            .then(({ sections }) =>
                                self.setFeatureDemo(feature, sections)
                            )
                    );
            },

            get asyncApp() {
                return self.app
                    ? LazyObservablePromise.resolve(JSON.parse(self.app))
                    : self
                          .asyncQuery(
                              `*[_type == "page"]{"slug": slug[]}`,
                              'landing_page'
                          )
                          .then(self.setApp);
            },

            asyncSubmenu(slug: string) {
                const existingSubmenu = self.pages.get(slug);

                if (existingSubmenu) {
                    return JSON.parse(existingSubmenu.content);
                }
                return self
                    .asyncPageQuery(slug)
                    .then(r => self.setPage(slug, r[0]));
            },

            get asyncRentalTraining() {
                return self.rentalTraining
                    ? LazyObservablePromise.resolve(
                          JSON.parse(self.rentalTraining)
                      )
                    : self
                          .asyncQuery(
                              '*[_type == "query" && slug.current == "rental_training"][0]{groq}',
                              'landing_page'
                          )
                          .later(({ groq }) =>
                              self
                                  .asyncQuery(groq, 'landing_page')
                                  .then(self.setRentalTraining)
                          );
            },

            get asyncRentalTrainingContent() {
                return self.rentalTrainingContent
                    ? LazyObservablePromise.resolve(
                          JSON.parse(self.rentalTrainingContent)
                      )
                    : self
                          .asyncQuery(
                              `*[_type == "trainingContent"]{
                            ...,
                            'content': content[]{
                                _type == 'video' => @->{
                                    ...,
                                    poster->,
                                    sources[] {
                                        device,
                                        format,
                                        "url": coalesce(
                                            source.asset->fileURL,
                                            url
                                        )
                                    }
                                },
                               _type == 'imageRef' => @->{..., image{..., asset->}},
                               _type == 'block' => @{...},
                            }
                        }`,
                              'landing_page'
                          )
                          .then(self.setRentalTrainingContent);
            },

            asyncBlog(query: string) {
                const existingBlog = self.blogs.get(query);
                if (existingBlog) {
                    return JSON.parse(existingBlog.content);
                }
                return self
                    .asyncQueryV2(query, 'blog')
                    .then(res => self.setBlog(query, res));
            },

            get asyncHeader() {
                return self.header
                    ? LazyObservablePromise.resolve(JSON.parse(self.header))
                    : self
                          .asyncQuery(
                              `*[_type == "header"][0]{
                        ...,
                        loginButton->,
                        signupButton->,
                        logo->,
                        'children': children[]{
                            _type == 'reference' => @->{
                                ...,page->{slug},section->{slug}},
                            _type != 'reference' => @{
                                ...,children[]->{
                                    ...,page->{slug},section->{_id}}},
                            }
                        }`,
                              'landing_page'
                          )
                          .then(self.setHeader);
            },

            get asyncFooter() {
                return self.footer
                    ? LazyObservablePromise.resolve(JSON.parse(self.footer))
                    : self
                          .asyncQuery(
                              `*[_type == "footer"][0]{
                        ...,
                        children[]{...select(
                            _type == "menuLink" => {
                                ...,page->{slug},section->{slug}},
                            _type == "menuItem" => {
                                ...,children[]-> {
                                    ...,page->{slug},section->{_id}}},
                        )}
                    }`,
                              'landing_page'
                          )
                          .then(self.setFooter);
            },

            get asyncPoll() {
                return self.poll
                    ? LazyObservablePromise.resolve(JSON.parse(self.poll))
                    : self
                          .asyncQuery(`*[_type == "poll"][0]`, 'app')
                          .then(self.setPoll);
            },

            asyncFaq(feature: FeatureFaq) {
                const featureToSlug: Record<FeatureFaq, string> = {
                    cap: 'faq-titulo-capitalizacao',
                    primePlan: 'faq-pricing-prime',
                    basicPlan: 'faq-pricing-basic',
                    blackPlan: 'faq-pricing-black',
                    upgradeCard: 'faq-upgrade-card',
                    upgradeBoleto: 'faq-upgrade-boleto',
                    referral: 'faq-referral',
                };

                const existingFaq = self.faqs.get(feature);

                if (existingFaq) {
                    return JSON.parse(existingFaq.faq);
                }

                return self
                    .asyncQuery(
                        `*[_type == "faqSection" &&
                            slug.current == "${featureToSlug[feature]}"][0]{
                            ...,
                            questions[]->{
                                ...,
                                answer[]{
                                    ...,
                                    content[]{
                                        ...,
                                        markDefs[]{
                                            ...,
                                            _type == "internalLink" =>{
                                                "slug": @.page->slug
                                            }
                                        }
                                    },
                                    markDefs[]{
                                        ...,
                                        _type == "internalLink" => {
                                            "slug": @.page->slug
                                        }
                                    }
                                }
                            }
                        }`,
                        'landing_page'
                    )
                    .then(faq => {
                        return self.setFaq(feature, faq);
                    });
            },
            get asyncAllPages() {
                if (self.loadedAllPages) {
                    return;
                }
                return self
                    .asyncQuery(
                        '*[_type == "query" && slug.current == "landing_page"][0]{groq}',
                        'landing_page'
                    )
                    .later(({ groq }) => {
                        return self.asyncQuery(groq, 'landing_page');
                    })
                    .then(self.setPages);
            },
        })
    )
    .views(self =>
        makeAsyncViews({
            asyncFaqs(features: FeatureFaq[]) {
                return Promise.all(
                    features.map(feature => self.asyncFaq(feature))
                );
            },
            asyncPage(slug: string) {
                const existingPage = self.pages.get(slug);

                if (existingPage) {
                    return JSON.parse(existingPage.content);
                }

                if (typeof window !== 'undefined' && !self.loadedAllPages) {
                    return self.asyncAllPages;
                }

                return self
                    .asyncPageQuery(slug)
                    .then(r => self.setPage(slug, r[0]));
            },
            asyncBlogPost(postSlug: string) {
                return self.asyncBlog(`
                    *[_type == "post" && url == "${postSlug}"]{
                        ...,
                        Content[]{
                            ...,
                            _type == "bannerRef"=>@-> {
                                ...,
                                imageRef->,
                                "postSlug": ^.^.url
                            },
                            _type == "takeaway" => {
                                ...,
                                takeaways[]{
                                    ...,
                                    markDefs[]{
                                        _type == "link" => {
                                            ...,
                                            "postSlug": ^.^.^.url
                                        }
                                    }
                                }
                            },
                            markDefs[]{
                                _type == "link" => {
                                    ...,
                                    "postSlug": ^.^.url
                                }
                            },
                            sources[]{
                                device,
                                format,
                                "url": coalesce(
                                    source.asset->fileURL,
                                    url
                                )
                            }
                        },
                        imageRef->,
                        author-> {
                            ...,
                            imageRef->
                        },
                        relatedPosts[]-> {
                            title,
                            url,
                            synopsis,
                            imageRef->
                        },
                        "relatedCategoryPosts": *[
                            _type == "post" &&
                            url != ^.url &&
                            category._ref == ^.category._ref
                        ]{
                            title,
                            url,
                            synopsis,
                            imageRef->
                        } | order(_createdAt desc)[0...4],
                        category->,
                        "footerBanner": footerBannerRef->
                    }[0]
                `);
            },
            asyncBlogPostFooter() {
                return self.asyncBlog(`
                    *[_type == "postFooter"]{
                        ...
                    }[0]
                `);
            },
            asyncAludeNewsPost(postSlug: string) {
                return self.asyncBlog(`
                    *[_type == "aludeNewsPost" && slug == "${postSlug}"]{
                        ...,
                        imageRef->,
                    }[0]
                `);
            },

            asyncPosts(
                pageNumber: number,
                categorySlug?: string,
                searchTerm?: string
            ) {
                const from = pageNumber * BLOG_POSTS_PAGE_SIZE;
                const to = (pageNumber + 1) * BLOG_POSTS_PAGE_SIZE + 1;
                const category = `category->slug == '${categorySlug}'`;
                const search = `title match '${searchTerm}'`;
                const query = `
                    *[
                        _type == "post" &&
                        type == 'post'
                        ${categorySlug ? `&& ${category}` : ''}
                        ${searchTerm ? `&& ${search}` : ''}
                    ] {
                        ...,
                        imageRef->,
                        category->,
                        _type == "takeaway" => {
                            ...,
                            takeaways[]{
                                ...,
                                markDefs[]{
                                    _type == "link" => {
                                        ...,
                                        "postSlug": ^.^.^.url
                                    }
                                }
                            }
                        },
                    }
                    | order(coalesce(dateOfWriting, _createdAt) desc)[${from}...${to}]
                `;
                return self.asyncBlog(query);
            },

            asyncAludeNewsPosts(pageNumber: number) {
                const from = pageNumber * BLOG_POSTS_PAGE_SIZE;
                const to = (pageNumber + 1) * BLOG_POSTS_PAGE_SIZE + 1;
                const query = `
                    *[_type == "aludeNewsPost"] {
                        ...,
                        imageRef->,
                        "isoSlug": (
                            string::split(slug, "-")[2] + '-' +
                            string::split(slug, "-")[1] + '-' +
                            string::split(slug, "-")[0]
                        )
                    }
                    | order(isoSlug desc)[${from}...${to}]
                `;
                return self.asyncBlog(query);
            },
        })
    );

const sanityController = (snapshot?: any): SanityController =>
    SanityControllerModel.create(snapshot, { client, clientV2 });

type SanityController = Instance<typeof SanityControllerModel>;
type SanitySnapshot = SnapshotIn<typeof SanityControllerModel>;

export { safeImageUrl, imageUrl, sanityController };
export type { SanityController, SanitySnapshot };
